import React, { useRef, useEffect, useState } from 'react';
import classes from './CustomDropdown.module.css';

export interface DropdownOption {
  key: string;
  value: string;
}

interface CustomDropdownProps {
  options: DropdownOption[];
  selectedValue: string;
  selectedLabel: string;
  onSelect: (key: string, label: string) => void;
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  error?: string;
  className?: string;
}

const CustomDropdown: React.FC<CustomDropdownProps> = ({
  options,
  selectedValue,
  selectedLabel,
  onSelect,
  placeholder = "Select an option",
  disabled = false,
  loading = false,
  error = "",
  className = ""
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleToggle = () => {
    if (!disabled && !loading) {
      setIsOpen(!isOpen);
    }
  };

  const handleOptionSelect = (option: DropdownOption) => {
    onSelect(option.key, option.value);
    setIsOpen(false);
  };

  const getDisplayText = () => {
    if (loading) return "Loading...";
    if (error) return error;
    if (options.length === 0) return "No options available";
    return selectedValue ? selectedLabel : placeholder;
  };

  return (
    <div
      ref={dropdownRef}
      className={`${classes.customDropdown} ${isOpen ? classes.open : ''} ${disabled ? classes.disabled : ''} ${className}`}
      onClick={handleToggle}
    >
      <div className={classes.dropdownHeader}>
        <span className={selectedValue ? classes.selectedValue : ''}>
          {getDisplayText()}
        </span>
        <svg
          className={`${classes.dropdownArrow} ${isOpen ? classes.dropdownArrowOpen : ''}`}
          width="14"
          height="10"
          viewBox="0 0 14 10"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M1 1L7 7L13 1"
            stroke="#6B7280"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      {isOpen && options.length > 0 && (
        <div className={classes.dropdownOptions}>
          {options.map((option) => (
            <div
              key={option.key}
              className={`${classes.dropdownOption} ${selectedValue === option.key ? classes.selected : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                handleOptionSelect(option);
              }}
            >
              {option.value}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CustomDropdown;